import {Calendar_First_Direction, Category_File_Type} from "@generated/graphql";

import {calendarFirstDirectionChoices} from "./constants";
import type {CategoryFileItem, FileTreeEntry, FileTreeFolder, MaterialFileItem} from "./types";

const getPathParts = (path: string) => path
  .split('/')
  .map((part) => part.trim())
  .filter(Boolean)
  .slice(0, 2);

const getOrCreateFolder = (
  entries: FileTreeEntry[],
  folders: Map<string, FileTreeFolder>,
  name: string,
  path: string,
) => {
  const folder = folders.get(name);

  if (folder) {
    return folder;
  }

  const newFolder: FileTreeFolder = {
    type: 'folder',
    name,
    path,
    children: [],
    folders: new Map(),
  };

  folders.set(name, newFolder);
  entries.push(newFolder);

  return newFolder;
};

const addFileToTree = (
  entries: FileTreeEntry[],
  folders: Map<string, FileTreeFolder>,
  file: MaterialFileItem,
  pathParts: string[],
  currentPath = '',
) => {
  const [folderName, ...nestedPathParts] = pathParts;

  if (!folderName) {
    entries.push({type: 'file', file});
    return;
  }

  const folderPath = `${currentPath}/${folderName}`;
  const folder = getOrCreateFolder(entries, folders, folderName, folderPath);

  addFileToTree(folder.children, folder.folders, file, nestedPathParts, folderPath);
};

export const buildFileTree = (files: MaterialFileItem[]) => {
  const entries: FileTreeEntry[] = [];
  const folders = new Map<string, FileTreeFolder>();

  files.forEach((file) => {
    addFileToTree(entries, folders, file, getPathParts(file.path));
  });

  return entries;
};

export const splitDirectionFileEntries = (entries: FileTreeEntry[]) => {
  const directionFiles: MaterialFileItem[] = [];
  const otherEntries: FileTreeEntry[] = [];

  entries.forEach((entry) => {
    if (entry.type === 'file' && entry.file.direction) {
      directionFiles.push(entry.file);
      return;
    }

    otherEntries.push(entry);
  });

  return {
    directionFiles,
    otherEntries,
  };
};

export const getDirectionFileGroups = (files: MaterialFileItem[]) => {
  const filesByDirection = files.reduce(
    (groups, file) => {
      if (file.direction) {
        groups[file.direction] = [...(groups[file.direction] || []), file];
      }

      return groups;
    },
    {} as Partial<Record<Calendar_First_Direction, MaterialFileItem[]>>,
  );

  return calendarFirstDirectionChoices
    .map((direction) => ({
      ...direction,
      files: filesByDirection[direction.id] || [],
    }))
    .filter((direction) => direction.files.length > 0);
};

export const getCategoryBannerUrl = (
  files: CategoryFileItem[],
  fileType: Category_File_Type,
) => files.find((file) => file.fileType === fileType)?.file.url;

export const getFolderAnchor = (path: string) => `material-files-${path}`;

export const getFolderLink = (path: string) => {
  const url = new URL(window.location.href);

  url.hash = getFolderAnchor(path);
  return url.toString();
};

const getCurrentHashElement = () => {
  const hash = window.location.hash.replace('#', '');

  if (!hash) {
    return null;
  }

  try {
    return document.getElementById(hash) || document.getElementById(decodeURIComponent(hash));
  } catch {
    return document.getElementById(hash);
  }
};

export const scrollToCurrentHashElement = () => {
  const element = getCurrentHashElement();

  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};
